import { $http } from '@/common/api/http.service';
import { $httpFhirApi } from '@/common/api/httpFhir.service';
import { buildAidboxQueryString, extractAidboxQueryData } from '@/composables/useAidboxQuery';
import { useAuthStore } from '@/stores/auth';

/**
 * Account-scoped, and it has to be: security.yaml only puts a JWT firewall in front of
 * `^/api/account/[0-9a-fA-F-]+/` (plus /api/system/ and /api/twilio/). A path outside those reaches
 * the `main` firewall, where `security: false` means the Bearer token this service attaches is
 * never read and the controller sees no authenticated user at all (#5343).
 *
 * The id comes from the store rather than a caller argument for the same reason the server takes
 * the account from the token: it is not the caller's to choose.
 */
const basePath = () => `/api/account/${useAuthStore().account?.id}/messaging`;

/**
 * The portal's equivalent, and it is a DIFFERENT prefix rather than a different route (#5333).
 *
 * `security.yaml` gives `^/portal/account/[0-9a-fA-F-]+/` its own firewall backed by
 * `portal_user_provider`. A portal request to an `/api/account/...` path would be authenticated
 * against the STAFF provider and fail; one to any other `/portal/...` path would reach the `main`
 * firewall, where `security: false` means the token is never read at all.
 */
const portalBasePath = () => `/portal/account/${useAuthStore().account?.id}/messaging`;

export default {
    /**
     * Send one message to a thread.
     *
     * Note there is no `recipient` argument, and adding one would do nothing: the server derives
     * the recipient list from the thread's active participant rows and never reads a recipient
     * field from the payload. That list is what enforces invitation-forward visibility, so it is
     * deliberately not the client's to set (#5343).
     *
     * Reads do NOT go through the server — they go direct to Aidbox, gated by the messaging-*
     * access policies. `getReactions` below is in this module for discoverability but still reads
     * through httpFhir.service for exactly that reason; see its docblock.
     */
    sendMessage: async ({ threadId, body, privateToStaff = false }) => {
        const res = await $http.post(`${basePath()}/threads/${threadId}/messages`, { body, privateToStaff });
        return res.data;
    },

    /**
     * Start a standalone internal staff-to-staff thread and post its opening message (#5338).
     *
     * No user JWT holds Create on BN_MessageThread or BN_MessageThreadParticipant, and no access
     * policy could grant it safely — a policy cannot read a request body, so "the thread you are
     * creating must list you as a participant" is not expressible as one. Hence a server endpoint.
     *
     * `topic` is the optional free-text subject line. Not to be confused with `subject`, which is
     * the CLIENT a thread is about — there is deliberately no `subject` argument here, because this
     * endpoint creates no client participant row and applies no chart-access check, so the server
     * refuses one. A client conversation goes through `openClientThread` below (#5332).
     *
     * `accessGroups` is an optional list of `BN_AccessGroup` ids restricting who may ever see the
     * conversation (#5405). Fixed at creation — there is deliberately no counterpart that changes them
     * afterwards, because every read re-evaluates the restriction and widening it would retroactively
     * hand someone messages sent before they qualified. The server refuses any participant, the author
     * included, holding none of them, so the picker narrowing itself is the affordance half only.
     *
     * @returns {Promise<{ threadId: string }>}
     */
    createThread: async ({ participantIds, body, topic, accessGroups }) => {
        const res = await $http.post(`${basePath()}/threads`, { participantIds, body, topic, accessGroups });
        return res.data;
    },

    /**
     * Open the caller's conversation with a client, creating it if there is not one yet (#5332).
     *
     * ONE call, not "search then create if empty", and that is deliberate on this side too: two calls
     * would let a double click create two conversations about the same client, and nothing
     * messaging-related is deletable, so the duplicate would be permanent and visible to the client.
     * The button also disables itself while this is in flight.
     *
     * The thread is per (caller, client) pair. A colleague's existing conversation with the same
     * client is NOT reused — joining a thread is an invitation with consequences for what history
     * becomes visible, and clicking Message on a chart is not a request to be added to somebody
     * else's conversation.
     *
     * `created` is informational; route to `threadId` either way.
     *
     * `accessGroups` restricts a NEWLY created conversation (#5405) and has no effect when an existing
     * one is reused — access groups are fixed at creation, so an existing conversation comes back as it
     * stands rather than re-restricted.
     *
     * @returns {Promise<{ threadId: string, created: boolean }>}
     */
    openClientThread: async (patientId, accessGroups = []) => {
        const res = await $http.post(`${basePath()}/threads/client`, { patientId, accessGroups });
        const threadId = res.data?.threadId;
        // Thrown rather than returned falsy, for the same reason getColleagues throws below: a
        // missing id would route to `/messages/undefined` and render a broken conversation page,
        // which reads as "your conversation is gone" rather than "that did not work".
        if (!threadId) {
            throw new Error('The client conversation response carried no thread id.');
        }
        return res.data;
    },

    /**
     * Which conversation `openClientThread` would REUSE for this caller, or null if it would create one
     * (#5405).
     *
     * Asked before the chart's Message action offers the access-group step, so the step only appears
     * when there is a conversation for it to restrict. Access groups are fixed at creation, so on the
     * reuse path the step could not take effect.
     *
     * A null `threadId` is a normal answer, NOT an error — unlike `openClientThread` above, which
     * throws on a missing id because it promised to produce one. Here "there isn't one yet" is the
     * question's whole point, so it is returned rather than thrown.
     *
     * This does not make the open a two-call flow: it reads, the POST still decides, and the POST is
     * still the single call that creates.
     *
     * @returns {Promise<{ threadId: string | null }>}
     */
    findClientThread: async (patientId) => {
        const res = await $http.get(`${basePath()}/threads/client`, { params: { patientId } });
        return { threadId: res.data?.threadId ?? null };
    },

    /**
     * Thread metadata plus the participant roster.
     *
     * The roster has to come from the server: `messaging-participant-read` lets a browser read only
     * its OWN participant row, so a client cannot enumerate a thread's members. The messages do
     * NOT come from here — see useMessageThread for why they are read straight from Aidbox.
     */
    getThread: async (threadId) => {
        const res = await $http.get(`${basePath()}/threads/${threadId}`);
        return res.data;
    },

    /**
     * Rename a thread, or clear its subject by passing an empty string.
     *
     * Server-side because no user JWT holds Update on BN_MessageThread — a policy cannot express
     * "you may retitle a thread you participate in" without reading its participant rows.
     *
     * @returns {Promise<{ threadId: string, topic: string|null }>}
     */
    updateThreadTopic: async (threadId, topic) => {
        const res = await $http.patch(`${basePath()}/threads/${threadId}`, { topic });
        return res.data;
    },

    /**
     * End a conversation, keeping every word of it (#5337).
     *
     * THERE IS NO REOPEN, and there is not meant to be: every surface treats a closed conversation
     * as terminal, so a clinician with more to say starts a new conversation from the chart. Adding
     * a counterpart here would be the first half of a transition nothing else expects.
     *
     * Idempotent — closing an already-closed conversation succeeds and changes nothing, so a double
     * click cannot produce an error.
     *
     * @returns {Promise<{ threadId: string, status: string|null, topic: string|null, lastMessageAt: string|null }>}
     */
    closeThread: async (threadId) => {
        const res = await $http.post(`${basePath()}/threads/${threadId}/close`, {});
        return res.data;
    },

    /**
     * A client's conversations, for their chart (#5337).
     *
     * Both open and closed, which is the point: `openClientThread` above deliberately never reuses a
     * closed conversation, so without this list a closed one is retained but reachable only from the
     * clinician's own inbox.
     *
     * METADATA ONLY — no message bodies, matching every other messaging read served by the server.
     * Throws on a response with no `threads` array rather than `?? []`, for the same reason
     * `getColleagues` does: an empty list is a legitimate answer here ("no conversations yet"), so a
     * silent fallback would be indistinguishable from it.
     *
     * @returns {Promise<Array<{ threadId: string, status: string|null, topic: string|null, lastMessageAt: string|null, participantNames: string[] }>>} newest activity first
     */
    getClientThreads: async (patientId) => {
        const res = await $http.get(`${basePath()}/clients/${patientId}/threads`);
        const threads = res.data?.threads;
        if (!Array.isArray(threads)) {
            throw new Error('The client conversation list response carried no threads array.');
        }
        return threads;
    },

    /**
     * Credentials for the live message stream (#5338).
     *
     * The gateway URL comes from the server rather than a VITE_ variable: the server already knows it
     * as BN_SOCK_PUBLIC_URL, and deriving it here would be a second source of truth to keep in step
     * across every deploy path — plus a new variable through both firebase-deploy workflows and a
     * GitHub secret.
     *
     * The token is separate from the login JWT because the gateway cannot verify that one — the app
     * signs RS256, and the gateway verifies connect tokens with the HS256 secret it shares with every
     * app that publishes through it.
     *
     * `channel` is informational: the connection carries only the token, and the channel it may join
     * is the one baked into it. Nothing the browser sends chooses a channel.
     *
     * @returns {Promise<{ gatewayUrl: string, channel: string, token: string, expiresIn: number }>}
     */
    getStreamToken: async () => {
        // Silent on failure. This runs unprompted whenever the layout mounts, so the default
        // interceptor would put a "You do not have permission to view this content." snackbar in
        // front of any user whose session has lapsed or who lacks the permission — for a background
        // connection they never asked for. useMessageStream handles the failure itself.
        const res = await $http.get(`${basePath()}/stream-token`, { errorHandle: false, skipLogging: true });
        return res.data;
    },

    /**
     * Add one colleague to an existing thread (#5339).
     *
     * Membership is server-written for the same reason threads are: no user JWT holds Create or
     * Update on BN_MessageThreadParticipant, and a policy cannot read a request body, so "you may
     * add someone to a thread you are in" is not expressible as one.
     *
     * Returns the whole roster, not just the added member — replace the roster with it rather than
     * merging, since the row may have been a reactivation of a previously removed member.
     *
     * @returns {Promise<{ participants: Array<{ practitionerId: string, displayName: string, active: boolean, joinedAt: string|null }> }>}
     */
    inviteParticipant: async ({ threadId, practitionerId }) => {
        const res = await $http.post(`${basePath()}/threads/${threadId}/participants`, { practitionerId });
        return res.data;
    },

    /**
     * The colleagues this user may message, for both staff pickers (#5374).
     *
     * Server-side because the deciding fact is unreachable from here: `message_with_other_users`
     * lives in account `Role` rows, and API Platform exposes `Role` only under `MANAGE_ROLES`. The
     * Aidbox `User` resource carries `active` and `practitioner` but no permissions, so no
     * combination of FHIR searches can answer "may this person take part in a staff conversation".
     *
     * This replaces the brand PractitionerRole + `User` search pair each dialog used to run. The
     * server applies the same restrictions those did — brand scope, an active linked `User`, and the
     * shared-location clause for a restricted caller — so nothing here needs to re-apply them.
     *
     * `threadId` names the conversation the picker is for (#5397), and changes WHICH permission the
     * server filters on: a client conversation is written with `message_with_clients`, so filtering
     * it on the staff permission would offer colleagues who reach the roster and cannot reply. Sent
     * as an id rather than a flag because the server reads the thread to decide — this side does not
     * get to choose which permission its picker is filtered on. Omit it for the new-message picker,
     * which starts an internal thread by construction.
     *
     * `accessGroups` is a list of `BN_AccessGroup` ids and narrows the list to colleagues holding at
     * least one of them (#5405). A colleague holding none could be added and would then see no
     * conversation, no messages and no inbox row, because every read re-evaluates the restriction — so
     * they are absent from the picker rather than offered and then silently shown nothing. Sent as a
     * comma-separated list because it is a GET; empty means no narrowing.
     *
     * @returns {Promise<Array<{ practitionerId: string, displayName: string }>>} sorted by name
     */
    getColleagues: async (brandId, threadId = '', accessGroups = []) => {
        const params = { brand: brandId };
        if (threadId) params.thread = threadId;
        if (accessGroups?.length) params.accessGroups = accessGroups.join(',');
        const res = await $http.get(`${basePath()}/colleagues`, { params });
        const colleagues = res.data?.colleagues;
        // A 200 without a `colleagues` array is a shape surprise, and `?? []` would have turned it into
        // an empty picker — indistinguishable from "nobody is eligible", which is now a normal outcome.
        // Thrown so the caller shows its load error instead of a silently empty list.
        if (!Array.isArray(colleagues)) {
            throw new Error('The colleague list response carried no colleagues array.');
        }
        return colleagues;
    },

    /**
     * Mark a conversation read for the signed-in user (#5376).
     *
     * The write itself is one field on the caller's own participant row, and this endpoint is the
     * only way it happens: no policy grants a browser a write on a participant row, because a
     * policy cannot read a request body and so could not hold the write down to lastReadAt. It also
     * has to ANNOUNCE — so the sender's read receipts update without a reload — and only the server
     * can publish to another user's realtime channel. There is no `lastReadAt` argument: the timestamp is the server's,
     * not the caller's, and no participant row id either, because it is not the caller's to choose.
     *
     * @returns {Promise<{ threadId: string, lastReadAt: string }>}
     */
    markThreadRead: async (threadId) => {
        const res = await $http.post(`${basePath()}/threads/${threadId}/read`, {});
        return res.data;
    },

    /**
     * Add or withdraw the caller's reaction to one message (#5376).
     *
     * Server-side because a policy cannot join a reaction to the reacted-to message's recipient
     * list, which is what decides who may react at all. Note there is no `practitionerId` argument:
     * whose reaction it is, is identity, and identity is resolved server-side.
     *
     * `remove` picks the verb. Withdrawing deactivates the caller's own row — nothing
     * messaging-related is deleted, reactions included.
     *
     * Returns the whole reaction set for the message, not just the change: replace rather than
     * merge, since the write may have reactivated a row that was already there.
     *
     * @returns {Promise<{ messageId: string, reactions: Array<{ emoji: string, practitionerId: string, displayName: string }> }>}
     */
    toggleReaction: async ({ messageId, emoji, remove = false }) => {
        const url = `${basePath()}/messages/${messageId}/reactions`;
        const res = remove ? await $http.delete(url, { data: { emoji } }) : await $http.post(url, { emoji });
        return res.data;
    },

    /**
     * Rewrite the body of a message the caller sent (#5376).
     *
     * PATCH, never PUT: a whole-resource replace would let a caller submit `recipient` alongside
     * the new text, and that list is frozen at send time and IS the read gate. The server's patch
     * names exactly `payload` and `editedAt`.
     *
     * @returns {Promise<{ id: string, body: string, editedAt: string }>}
     */
    editMessage: async ({ messageId, body }) => {
        const res = await $http.patch(`${basePath()}/messages/${messageId}`, { body });
        return res.data;
    },

    /**
     * File a conversation against one or more clients, and optionally against one of their forms (#5406).
     *
     * Server-side because a policy cannot read a request body, so "you may file a conversation you take
     * part in onto a chart you can see" is not expressible as one — and because the two facts it turns
     * on are not the browser's to assert: whether the caller holds an active participant row, and
     * whether they may see each chart.
     *
     * ASSOCIATION IS FILING, NOT MEMBERSHIP. Nobody is added to the conversation, no `recipient` entry
     * is written, and an associated client can never read the conversation from the portal. What it
     * does do is let staff holding that client's chart read the conversation, whether or not they are
     * in it — retroactively, for messages sent long ago. That is why the dialog warns before confirming
     * rather than after: the widening is the point, and the person filing is the only one in a position
     * to decide it is appropriate.
     *
     * There is no `subject`, `accessGroup` or `participantIds` argument, and adding one would do
     * nothing: the server patches `BN_MessageThread.about` and nothing else.
     *
     * @returns {Promise<{ id: string, about: Array<{ resourceType: string, id: string }> }>}
     */
    associateThread: async ({ threadId, patientIds = [], questionnaireResponseId = null }) => {
        const res = await $http.post(`${basePath()}/threads/${threadId}/associations`, { patientIds, questionnaireResponseId });
        return res.data;
    },

    /**
     * Withdraw one association (#5406).
     *
     * DELETE the call, but nothing is deleted: the server patches `about`, and the array as it stood is
     * retained in the conversation's own resource history the way an edited message body is. Nothing
     * messaging-related is deletable, associations included.
     *
     * `reference` is `Patient/{id}` or `QuestionnaireResponse/{id}` — typed, because a bare id would be
     * ambiguous between a client and a form. Sent in the body for the same reason `toggleReaction`
     * sends its emoji there: a slash in a path segment is not a path segment.
     *
     * Idempotent — removing an association that is already gone succeeds and changes nothing.
     *
     * @returns {Promise<{ id: string, about: Array<{ resourceType: string, id: string }> }>}
     */
    removeThreadAssociation: async ({ threadId, reference }) => {
        const res = await $http.delete(`${basePath()}/threads/${threadId}/associations`, { data: { reference } });
        return res.data;
    },

    /**
     * Every standing reaction in one conversation (#5376).
     *
     * The one READ in this module, and it deliberately does not go through the server API: like the
     * messages themselves, reactions must be read under the caller's own JWT so Aidbox decides what
     * they may see. `AidboxQuery/message-reactions` anchors every row on the reacted-to message's
     * frozen `recipient` list containing `{{user.practitioner.id}}`, so a message the caller cannot
     * read contributes no reactions — the same predicate that gives invitation-forward visibility.
     * Reading these through the API instead would authenticate as the unrestricted `api-platform`
     * client and leak the existence of messages the caller was never sent.
     *
     * One query per conversation rather than one per message, so a long thread is not an N+1.
     *
     * @returns {Promise<Array<{ messageId: string, emoji: string, practitionerId: string, displayName: string }>>}
     */
    getReactions: async (threadId) => {
        const qs = buildAidboxQueryString({ thread: threadId, _limit: 1000, _offset: 0 });
        const result = await $httpFhirApi.get(`/$query/message-reactions?${qs}`);
        return extractAidboxQueryData(result).map((row) => ({
            messageId: row.message_id,
            emoji: row.emoji,
            practitionerId: row.practitioner_id,
            // Falls back to the id rather than blank, as the roster does.
            displayName: row.display_name || row.practitioner_id,
        }));
    },

    /**
     * The client's own conversations, from the patient portal (#5333).
     *
     * METADATA ONLY, and deliberately no message bodies. The portal's one messaging access policy
     * grants `Communication` reads where `recipient = .user.portalUser.id` AND
     * `privateToStaff = 'false'`, and grants nothing at all on `BN_MessageThread` or
     * `BN_MessageThreadParticipant` — so the list, the status and the staff names have to be handed
     * to the client, while the messages are read straight from Aidbox under the client's own token
     * (see usePortalMessages). Serving the bodies from here would authenticate as the unrestricted
     * `api-platform` client and hand over the whole thread, including messages sent before the
     * client joined and private staff ones.
     *
     * @returns {Promise<{ threads: Array<{ threadId: string, status: string|null, lastMessageAt: string|null, unread: boolean,
     *                     staff: Array<{ practitionerId: string, displayName: string, active: boolean }> }> }>}
     */
    getPortalThreads: async () => {
        const res = await $http.get(`${portalBasePath()}/threads`);
        return res.data;
    },

    /**
     * Reply to the care team from the portal (#5333).
     *
     * There is no `privateToStaff` argument, and adding one would be refused: a client can never
     * author a message hidden from the client, so the server does not accept the flag at all rather
     * than accepting and rejecting it.
     */
    sendPortalMessage: async ({ threadId, body }) => {
        const res = await $http.post(`${portalBasePath()}/threads/${threadId}/messages`, { body });
        return res.data;
    },

    /**
     * Mark a conversation read for the signed-in client (#5333).
     *
     * Server-side because the portal holds no access to `BN_MessageThreadParticipant` whatsoever —
     * unlike staff, who still have `messaging-read-receipt`. The endpoint patches the client's own
     * row and nothing else.
     */
    markPortalThreadRead: async (threadId) => {
        const res = await $http.post(`${portalBasePath()}/threads/${threadId}/read`, {});
        return res.data;
    },

    /**
     * Credentials for the client's live message stream (#5333).
     *
     * Same shape and the same silence-on-failure as getStreamToken above — this runs unprompted
     * whenever the portal layout mounts, so the default interceptor would put a permission snackbar
     * in front of a client whose session has lapsed, for a connection they never asked for.
     *
     * @returns {Promise<{ gatewayUrl: string, channel: string, token: string, expiresIn: number }>}
     */
    getPortalStreamToken: async () => {
        const res = await $http.get(`${portalBasePath()}/stream-token`, { errorHandle: false, skipLogging: true });
        return res.data;
    },
};
